import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Plus, Trash2, Eye } from "lucide-react";

export default function ContactForm({ open, onOpenChange, contact, groups, senderAccounts = [], onSuccess }) {
  const isEdit = !!contact;
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [visibility, setVisibility] = useState("only_me");
  const [visibilitySenderIds, setVisibilitySenderIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (contact) {
        setEmail(contact.email || "");
        setName(contact.name || "");
        setTags(contact.tags || []);
        setSelectedGroups(contact.group_ids || []);
        setCustomFields(contact.custom_fields || []);
        setVisibility(contact.visibility || "only_me");
        setVisibilitySenderIds(contact.visibility_sender_ids || []);
      } else {
        setEmail("");
        setName("");
        setTags([]);
        setSelectedGroups([]);
        setCustomFields([]);
        setVisibility("only_me");
        setVisibilitySenderIds([]);
      }
      setTagInput("");
    }
  }, [open, contact]);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
    }
    setTagInput("");
  };

  const removeTag = (t) => setTags(tags.filter((x) => x !== t));

  const toggleGroup = (gid) => {
    setSelectedGroups((prev) =>
      prev.includes(gid) ? prev.filter((id) => id !== gid) : [...prev, gid]
    );
  };

  const addCustomField = () => {
    setCustomFields([...customFields, { key: "", value: "" }]);
  };

  const updateCustomField = (idx, field, val) => {
    setCustomFields(customFields.map((cf, i) => (i === idx ? { ...cf, [field]: val } : cf)));
  };

  const removeCustomField = (idx) => {
    setCustomFields(customFields.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      email: email.trim(),
      name: name.trim(),
      tags,
      group_ids: selectedGroups,
      custom_fields: customFields.filter((cf) => cf.key.trim()),
      visibility,
      visibility_sender_ids: visibility === "specific_senders" ? visibilitySenderIds : [],
    };

    try {
      if (isEdit) {
        await api.put(`/contacts/${contact.contact_id}`, payload);
        toast.success("Contact updated");
      } else {
        await api.post("/contacts", payload);
        toast.success("Contact created");
      }
      onSuccess();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="contact-form-dialog">
        <DialogHeader>
          <DialogTitle data-testid="contact-form-title">
            {isEdit ? "Edit contact" : "Add contact"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div className="space-y-1.5">
            <Label htmlFor="contact-email" className="text-xs font-medium text-zinc-600">
              Email address
            </Label>
            <Input
              id="contact-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              required
              data-testid="contact-email-input"
            />
          </div>

          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="contact-name" className="text-xs font-medium text-zinc-600">
              Name (optional)
            </Label>
            <Input
              id="contact-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              data-testid="contact-name-input"
            />
          </div>

          {/* Tags */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-zinc-600">Tags</Label>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {tags.map((t) => (
                <Badge key={t} variant="secondary" className="text-xs pr-1 gap-1">
                  {t}
                  <button type="button" onClick={() => removeTag(t)} className="hover:text-red-500">
                    <X size={11} />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="Add a tag..."
                className="h-8 text-sm"
                data-testid="contact-tag-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={addTag} data-testid="contact-tag-add-btn">
                <Plus size={13} />
              </Button>
            </div>
          </div>

          {/* Groups */}
          {groups.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-zinc-600">Groups</Label>
              <div className="space-y-1.5 max-h-32 overflow-y-auto rounded-md border border-zinc-200 p-2">
                {groups.map((g) => (
                  <label
                    key={g.group_id}
                    className="flex items-center gap-2 py-1 px-1 rounded hover:bg-zinc-50 cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedGroups.includes(g.group_id)}
                      onCheckedChange={() => toggleGroup(g.group_id)}
                      data-testid={`contact-group-check-${g.group_id}`}
                    />
                    <span className="text-sm text-zinc-700">{g.name}</span>
                    {g.contact_count != null && (
                      <span className="text-[10px] text-zinc-500 ml-auto">{g.contact_count}</span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Custom Fields */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-zinc-600">Custom fields</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={addCustomField}
                className="text-xs h-6 px-2"
                data-testid="add-custom-field-btn"
              >
                <Plus size={12} className="mr-1" /> Add field
              </Button>
            </div>
            {customFields.map((cf, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input
                  value={cf.key}
                  onChange={(e) => updateCustomField(idx, "key", e.target.value)}
                  placeholder="Field name"
                  className="h-8 text-sm w-1/3"
                  data-testid={`custom-field-key-${idx}`}
                />
                <Input
                  value={cf.value}
                  onChange={(e) => updateCustomField(idx, "value", e.target.value)}
                  placeholder="Value"
                  className="h-8 text-sm flex-1"
                  data-testid={`custom-field-value-${idx}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-zinc-500 hover:text-red-500"
                  onClick={() => removeCustomField(idx)}
                >
                  <Trash2 size={13} />
                </Button>
              </div>
            ))}
          </div>

          {/* Visibility */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-zinc-600 flex items-center gap-1.5">
              <Eye size={12} /> Visibility
            </Label>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger className="h-9" data-testid="contact-visibility-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="only_me">Only me (private)</SelectItem>
                <SelectItem value="all_senders">All sender accounts</SelectItem>
                <SelectItem value="specific_senders">Specific sender accounts</SelectItem>
              </SelectContent>
            </Select>
            {visibility === "only_me" && (
              <p className="text-[10px] text-zinc-500">Only you can see and use this contact</p>
            )}
            {visibility === "all_senders" && (
              <p className="text-[10px] text-zinc-500">Available to all sender accounts, including future ones</p>
            )}
            {visibility === "specific_senders" && (
              <div className="space-y-1.5 mt-1">
                <p className="text-[10px] text-zinc-500">Select which sender accounts can access this contact</p>
                {senderAccounts.length > 0 ? (
                  <div className="max-h-28 overflow-y-auto rounded-md border border-zinc-200 p-2 space-y-1">
                    {senderAccounts.map((sa) => (
                      <label key={sa.alias_id} className="flex items-center gap-2 py-0.5 px-1 rounded hover:bg-zinc-50 cursor-pointer">
                        <Checkbox
                          checked={visibilitySenderIds.includes(sa.alias_id)}
                          onCheckedChange={(checked) => {
                            setVisibilitySenderIds((prev) =>
                              checked ? [...prev, sa.alias_id] : prev.filter((id) => id !== sa.alias_id)
                            );
                          }}
                          data-testid={`visibility-sender-${sa.alias_id}`}
                        />
                        <span className="text-xs text-zinc-600 font-mono truncate">{sa.alias_address}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-[10px] text-zinc-500 italic">No sender accounts available</p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving} data-testid="contact-form-submit">
              {saving ? "Saving..." : isEdit ? "Update contact" : "Add contact"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
